/**
 * Date and time inputs that always read **dd/mm/jjjj** and **24-hour**, whatever
 * the phone is set to.
 *
 * `<input type="date">` and `<input type="time">` draw their text in the
 * *browser's own application locale*, and nothing in the markup overrides it:
 * not `lang` on the input, not `lang` on the document, not the page language,
 * not `Accept-Language`. Measured in Chromium, 2026-09-15 — `lang="nl-NL"`,
 * `lang="en-GB"` and a browser context with `locale: 'nl-NL'` all still drew
 * `09/15/2026`. So a Dutch household on a phone they happen to run in English
 * gets American dates and `09:00 AM` in a Dutch app, and no attribute can say
 * otherwise.
 *
 * The fix keeps the native input — it is what opens the OS picker, and on a
 * phone picking beats typing — and paints our own text over it, hiding the
 * native text. Desktop Chromium/Safari draw that text in `::-webkit-datetime-edit`;
 * **iOS Safari uses `::-webkit-date-and-time-value` instead**, so both are hidden
 * (in styles.css) or the native `09:00 AM` shows through under our face on a
 * phone — the case that actually matters here. The whole thing is gated behind
 * `@supports selector(::-webkit-datetime-edit)`, so the overlay only ever
 * appears where the native text can actually be hidden; a browser without that
 * pseudo-element (Firefox) keeps its own rendering rather than stacking two
 * values on top of each other.
 *
 * The face is painted from a `data-face` attribute by CSS, **not** rendered as a
 * child node. A text node would join the wrapping `<label>`'s contents, and an
 * implicitly-labelled control takes its accessible name from exactly that — so
 * "Datum" silently became "Datum 15/09/2026", duplicating a value the input
 * already announces. Generated content is not in the DOM at all, so there is
 * nothing to leak and nothing to hide with `aria-hidden`.
 *
 * The value contract is unchanged either way: `YYYY-MM-DD` and `HH:MM`, the
 * formats the rest of the app already speaks and the ones the native inputs use
 * regardless of how they display them.
 */

interface FieldProps {
  value: string;
  onChange: (next: string) => void;
  min?: string;
  ariaLabel?: string;
  disabled?: boolean;
}

export function DateField({ value, onChange, min, ariaLabel, disabled }: FieldProps) {
  return (
    <span className="dtfield" data-face={formatDayMonthYear(value)}>
      <input
        type="date"
        value={value}
        min={min}
        aria-label={ariaLabel}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    </span>
  );
}

export function TimeField({ value, onChange, ariaLabel, disabled }: FieldProps) {
  return (
    <span className="dtfield" data-face={formatTime24(value)}>
      <input
        type="time"
        value={value}
        aria-label={ariaLabel}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    </span>
  );
}

/** `2026-09-15` → `15/09/2026`; anything unparseable → the empty-field hint. */
export function formatDayMonthYear(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : 'dd/mm/jjjj';
}

/**
 * `09:00` → `09:00`.
 *
 * A time input's `value` is *already* 24-hour `HH:MM` whatever it displays — the
 * AM/PM is presentation only — so this is a pass-through with seconds trimmed
 * and an empty hint. It exists so the face has one source, and so the 24-hour
 * guarantee is stated somewhere rather than assumed.
 */
export function formatTime24(value: string): string {
  const m = /^(\d{2}):(\d{2})/.exec(value);
  return m ? `${m[1]}:${m[2]}` : '--:--';
}
